import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  GraduationCap, Play, Pause, ArrowLeft, ArrowRight, CaretRight,
  Check, X, BookOpen, Trophy, Clock, Target, Lightning, Video,
  SpeakerHigh, FileText, CaretDown, SkipForward, Rewind, Star, Rocket
} from "@phosphor-icons/react";

const STATUS_COLORS = { not_started: "bg-gray-100 text-gray-600", in_progress: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700" };

export default function AcademyPage() {
  const { user } = useAuth();
  const [view, setView] = useState("dashboard");
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const audioRef = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState(1);
  const [lessonStartTime, setLessonStartTime] = useState(null);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const [modRes, progRes] = await Promise.all([api.get("/academy/modules"), api.get("/academy/progress")]);
      setModules(modRes.data.modules || []);
      setProgress(progRes.data);
    } catch { toast.error("Failed to load academy"); }
    finally { setLoading(false); }
  };

  const openModule = async (moduleId) => {
    try {
      const { data } = await api.get(`/academy/modules/${moduleId}`);
      setSelectedModule(data);
      setView("module");
      await api.post(`/academy/progress/${moduleId}/start`).catch(() => {});
    } catch { toast.error("Failed to load module"); }
  };

  const openLesson = (lesson) => {
    setSelectedLesson(lesson);
    setLessonStartTime(Date.now());
    setView("lesson");
    setAudioPlaying(false);
  };

  const completeLesson = async () => {
    if (!selectedModule || !selectedLesson) return;
    const timeSpent = lessonStartTime ? Math.round((Date.now() - lessonStartTime) / 1000) : 0;
    try {
      await api.post(`/academy/progress/${selectedModule.id}/lesson/${selectedLesson.id}/complete`, { time_spent_seconds: timeSpent });
      toast.success("Lesson completed");
      const { data } = await api.get(`/academy/modules/${selectedModule.id}`);
      setSelectedModule(data);
      const lessons = (data.lessons || []).sort((a, b) => a.order - b.order);
      const idx = lessons.findIndex(l => l.id === selectedLesson.id);
      if (idx < lessons.length - 1) {
        openLesson(lessons[idx + 1]);
      } else {
        // Last lesson — go back to module view so user can take assessment
        setSelectedLesson(null);
        setAudioPlaying(false);
        setView("module");
        if (data.questions?.length > 0) {
          toast.success("All lessons complete! You can now take the Knowledge Check.", { duration: 4000 });
        }
      }
    } catch { toast.error("Failed to mark lesson complete"); }
  };

  const startAssessment = () => {
    setAnswers({});
    setAssessmentResult(null);
    setView("assessment");
  };

  const submitAssessment = async () => {
    if (!selectedModule) return;
    try {
      const { data } = await api.post(`/academy/modules/${selectedModule.id}/assessment/submit`, { answers });
      setAssessmentResult(data);
      if (data.module_completed) {
        toast.success("Module completed! Well done!");
        loadDashboard();
        // Check if all academy modules are now done (advances onboarding)
        try {
          const statusRes = await api.get("/onboarding/status");
          if (statusRes.data.onboarding_status === "active") {
            toast.success("Congratulations! You've completed all training. Welcome to the Recruitment Hub!", { duration: 5000 });
          }
        } catch { /* silently ignore status check error */ }
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
  };

  const generateCertificate = async () => {
    try {
      const res = await api.post("/academy/certificates/generate");
      toast.success("Certificate generated!");
      const { data } = await api.get("/academy/certificates");
      setCertificates(data.certificates || []);
      // Auto-download the newly generated certificate
      if (res.data?.id) {
        await downloadCertPdf(res.data.id);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot generate certificate yet"); }
  };

  const loadAndDownloadCert = async () => {
    try {
      const { data } = await api.get("/academy/certificates");
      const certs = data.certificates || [];
      if (certs.length > 0) {
        await downloadCertPdf(certs[0].id);
      } else {
        toast.error("No certificate found");
      }
    } catch { toast.error("Failed to load certificates"); }
  };

  const goToNextModule = async () => {
    if (!selectedModule) return;
    const currentOrder = selectedModule.order || 0;
    const next = modules
      .filter(m => m.order > currentOrder && (m.progress?.status || "not_started") !== "completed")
      .sort((a, b) => a.order - b.order)[0];
    if (next) {
      await openModule(next.id);
    } else {
      // All modules complete — go to dashboard
      setSelectedModule(null);
      setView("dashboard");
      loadDashboard();
      toast.success("Congratulations! You've completed all available modules!");
    }
  };

  const goToRecruitmentHub = () => {
    window.location.href = "/";
  };

  const downloadCertPdf = async (certId) => {
    try {
      const res = await api.get(`/academy/certificates/${certId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `certificate-${certId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download"); }
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (audioPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setAudioPlaying(!audioPlaying);
  };

  const setSpeed = (s) => {
    setAudioSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  };

  // ============ LESSON VIEW ============
  if (view === "lesson" && selectedLesson && selectedModule) {
    const lessons = [...(selectedModule.lessons || [])].sort((a, b) => a.order - b.order);
    const idx = lessons.findIndex(l => l.id === selectedLesson.id);
    const isCompleted = (selectedModule.progress?.lessons_completed || []).includes(selectedLesson.id);
    const isLastLesson = idx === lessons.length - 1;
    return (
      <div data-testid="academy-lesson-view" className="space-y-4 max-w-4xl">
        <button onClick={() => { setView("module"); setSelectedLesson(null); setAudioPlaying(false); }} className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1" data-testid="back-to-module"><ArrowLeft size={12} /> Back to {selectedModule.title}</button>
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-[#0F172A]">{selectedLesson.title}</h1>
          <span className="text-xs text-[#6B7280]">Lesson {idx + 1} of {lessons.length}</span>
        </div>

        {/* Video */}
        {selectedLesson.video_url && (
          <div className="bg-black rounded-lg overflow-hidden aspect-video">
            <video src={selectedLesson.video_url} controls className="w-full h-full" data-testid="lesson-video" />
          </div>
        )}

        {/* Voiceover Player */}
        {(selectedLesson.audio_url || selectedLesson.voiceover_script) && (
          <div className="bg-[#0F172A] rounded-lg p-4" data-testid="voiceover-player">
            <div className="flex items-center gap-3 mb-2">
              <SpeakerHigh size={18} className="text-[#C5A059]" weight="duotone" />
              <span className="text-xs text-[#C5A059] font-semibold uppercase tracking-wider">Voiceover Narration</span>
            </div>
            {selectedLesson.audio_url && (
              <>
                <audio ref={audioRef} src={selectedLesson.audio_url} onEnded={() => setAudioPlaying(false)} />
                <div className="flex items-center gap-3 mb-3">
                  <button onClick={toggleAudio} className="w-10 h-10 rounded-full bg-[#C5A059] flex items-center justify-center hover:bg-[#B8933F] transition" data-testid="voiceover-play-btn">
                    {audioPlaying ? <Pause size={18} className="text-white" weight="bold" /> : <Play size={18} className="text-white" weight="bold" />}
                  </button>
                  <div className="flex gap-1">
                    {[0.75, 1, 1.25, 1.5, 2].map(s => (
                      <button key={s} onClick={() => setSpeed(s)} className={`px-2 py-1 rounded text-[10px] font-medium transition ${audioSpeed === s ? "bg-[#C5A059] text-white" : "bg-[#1E293B] text-[#94A3B8] hover:bg-[#334155]"}`}>{s}x</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {!selectedLesson.audio_url && selectedLesson.voiceover_script && (
              <p className="text-xs text-[#94A3B8] italic mb-2">Audio not yet generated. Script preview below:</p>
            )}
            {selectedLesson.voiceover_script && (
              <details className="group">
                <summary className="text-xs text-[#94A3B8] cursor-pointer hover:text-white transition">View transcript</summary>
                <p className="text-sm text-[#CBD5E1] mt-2 leading-relaxed">{selectedLesson.voiceover_script}</p>
              </details>
            )}
          </div>
        )}

        {/* Written Content */}
        <div className="bg-white rounded-lg border p-6">
          <div className="prose prose-sm max-w-none text-[#0F172A]" style={{ whiteSpace: "pre-wrap" }}>
            {selectedLesson.written_content?.split("\n").map((line, i) => {
              if (line.startsWith("**") && line.endsWith("**")) return <h3 key={i} className="text-sm font-bold text-[#0F172A] mt-4 mb-1">{line.replace(/\*\*/g, "")}</h3>;
              if (line.startsWith("- ")) return <li key={i} className="text-sm ml-4 text-[#374151]">{line.slice(2)}</li>;
              if (line.trim() === "") return <br key={i} />;
              return <p key={i} className="text-sm text-[#374151] leading-relaxed">{line.replace(/\*\*(.*?)\*\*/g, "$1")}</p>;
            })}
          </div>
        </div>

        {/* Key Points */}
        {selectedLesson.key_points?.length > 0 && (
          <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-lg p-4" data-testid="key-points">
            <h3 className="text-xs font-semibold text-[#C5A059] uppercase mb-2 flex items-center gap-1"><Lightning size={14} weight="bold" /> Key Points</h3>
            <ul className="space-y-1.5">
              {selectedLesson.key_points.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#0F172A]"><Check size={14} className="text-[#C5A059] mt-0.5 flex-shrink-0" weight="bold" />{p}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Practical Task */}
        {selectedLesson.practical_task && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-[#1E3A8A] uppercase mb-2 flex items-center gap-1"><Target size={14} weight="bold" /> Practical Task</h3>
            <p className="text-sm text-[#0F172A]">{selectedLesson.practical_task}</p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" disabled={idx === 0} onClick={() => openLesson(lessons[idx - 1])} className="gap-1"><ArrowLeft size={14} /> Previous</Button>
          <div className="flex gap-2 items-center">
            {!isCompleted && (
              <Button onClick={completeLesson} className="bg-[#10B981] hover:bg-[#059669] gap-1" data-testid="complete-lesson-btn">
                <Check size={14} weight="bold" /> {idx < lessons.length - 1 ? "Complete & Next" : "Mark Complete"}
              </Button>
            )}
            {isCompleted && idx < lessons.length - 1 && (
              <Button onClick={() => openLesson(lessons[idx + 1])} className="bg-[#0F172A] gap-1" data-testid="next-lesson-btn">
                Next Lesson <ArrowRight size={14} />
              </Button>
            )}
            {isCompleted && idx === lessons.length - 1 && (
              <Button onClick={() => { setView("module"); setSelectedLesson(null); setAudioPlaying(false); }} className="bg-[#C5A059] hover:bg-[#B8933F] gap-1" data-testid="continue-to-module-btn">
                {selectedModule.questions?.length > 0 ? "Continue to Assessment" : "Back to Module"} <ArrowRight size={14} />
              </Button>
            )}
            {isCompleted && <span className="text-xs text-green-600 font-medium flex items-center gap-1 ml-1"><Check size={14} weight="bold" /> Done</span>}
          </div>
        </div>
      </div>
    );
  }

  // ============ ASSESSMENT VIEW ============
  if (view === "assessment" && selectedModule) {
    const questions = selectedModule.questions || [];
    return (
      <div data-testid="academy-assessment" className="space-y-4 max-w-3xl">
        <button onClick={() => setView("module")} className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1"><ArrowLeft size={12} /> Back to {selectedModule.title}</button>
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-[#0F172A]">Knowledge Check — {selectedModule.title}</h1>
          <span className="text-xs text-[#6B7280]">Pass mark: {selectedModule.pass_mark || 80}%</span>
        </div>

        {assessmentResult ? (
          <div className="space-y-4" data-testid="assessment-result">
            <div className={`rounded-lg p-6 text-center ${assessmentResult.passed ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${assessmentResult.passed ? "bg-green-100" : "bg-red-100"}`}>
                {assessmentResult.passed ? <Trophy size={32} className="text-green-600" weight="bold" /> : <X size={32} className="text-red-500" weight="bold" />}
              </div>
              <h2 className="text-xl font-bold">{assessmentResult.passed ? "Assessment Passed!" : "Not Quite — Try Again"}</h2>
              <p className="text-3xl font-bold mt-2" style={{ color: assessmentResult.passed ? "#10B981" : "#EF4444" }}>{assessmentResult.score}%</p>
              <p className="text-sm text-[#6B7280] mt-1">Attempt {assessmentResult.attempt} of {assessmentResult.max_attempts}</p>
              {assessmentResult.module_completed && <p className="text-sm text-green-600 font-semibold mt-2">Module completed!</p>}
            </div>
            {assessmentResult.feedback?.map((f, i) => (
              <div key={i} className={`rounded-lg p-4 border ${f.correct ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <p className="text-sm font-medium text-[#0F172A]">{questions[i]?.question}</p>
                <p className="text-xs mt-1">{f.correct ? <span className="text-green-600">Correct</span> : <span className="text-red-500">Incorrect — Correct answer: {f.correct_answer}</span>}</p>
                {f.explanation && <p className="text-xs text-[#6B7280] mt-1">{f.explanation}</p>}
              </div>
            ))}
            <div className="flex gap-2">
              {!assessmentResult.passed && assessmentResult.attempt < assessmentResult.max_attempts && (
                <Button onClick={() => { setAnswers({}); setAssessmentResult(null); }} className="bg-[#0F172A]" data-testid="retry-assessment-btn">Retry Assessment</Button>
              )}
              {assessmentResult.passed && (
                <Button onClick={goToNextModule} className="bg-[#C5A059] hover:bg-[#B8933F] gap-1" data-testid="next-module-btn">
                  Next Module <ArrowRight size={14} />
                </Button>
              )}
              <Button variant="outline" onClick={() => setView("module")}>Back to Module</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((q, qi) => (
              <div key={q.id} className="bg-white rounded-lg border p-4" data-testid={`question-${qi}`}>
                <p className="text-sm font-medium text-[#0F172A] mb-3">{qi + 1}. {q.question}</p>
                {q.type === "true_false" ? (
                  <div className="flex gap-2">
                    {["true", "false"].map(v => (
                      <button key={v} onClick={() => setAnswers({...answers, [q.id]: v})} className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${answers[q.id] === v ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#374151] hover:bg-gray-50"}`}>{v === "true" ? "True" : "False"}</button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(q.options || []).map((opt, oi) => (
                      <button key={oi} onClick={() => setAnswers({...answers, [q.id]: opt})} className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition ${answers[q.id] === opt ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#374151] hover:bg-gray-50"}`}>{opt}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Button onClick={submitAssessment} disabled={Object.keys(answers).length < questions.length} className="bg-[#C5A059] hover:bg-[#B8933F] w-full h-12" data-testid="submit-assessment-btn">Submit Assessment</Button>
          </div>
        )}
      </div>
    );
  }

  // ============ MODULE VIEW ============
  if (view === "module" && selectedModule) {
    const lessons = selectedModule.lessons || [];
    const questions = selectedModule.questions || [];
    const prog = selectedModule.progress || {};
    const lessonsCompleted = prog.lessons_completed || [];
    const allLessonsDone = lessons.every(l => lessonsCompleted.includes(l.id));
    return (
      <div data-testid="academy-module-view" className="space-y-4 max-w-4xl">
        <button onClick={() => { setView("dashboard"); setSelectedModule(null); loadDashboard(); }} className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1" data-testid="back-to-academy"><ArrowLeft size={12} /> Back to Academy</button>

        {/* Module Header */}
        <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] rounded-xl p-6 text-white">
          <span className="text-[10px] text-[#C5A059] font-semibold uppercase tracking-wider">Module {selectedModule.order}</span>
          <h1 className="text-xl font-bold mt-1">{selectedModule.title}</h1>
          <p className="text-sm text-[#94A3B8] mt-1">{selectedModule.description}</p>
          <div className="flex gap-4 mt-3">
            <span className="text-xs text-[#94A3B8] flex items-center gap-1"><Clock size={12} /> {selectedModule.estimated_duration_minutes} min</span>
            <span className="text-xs text-[#94A3B8] flex items-center gap-1"><BookOpen size={12} /> {lessons.length} lessons</span>
            <span className="text-xs text-[#94A3B8] flex items-center gap-1"><FileText size={12} /> {questions.length} questions</span>
            {prog.status === "completed" && <span className="text-xs text-green-400 flex items-center gap-1"><Check size={12} weight="bold" /> Completed</span>}
          </div>
        </div>

        {/* Objectives */}
        {selectedModule.objectives?.length > 0 && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-xs font-semibold text-[#6B7280] uppercase mb-2">Module Objectives</h3>
            <ul className="space-y-1">
              {selectedModule.objectives.map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#374151]"><Target size={14} className="text-[#C5A059] mt-0.5 flex-shrink-0" />{o}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Lessons */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-[#0F172A]">Lessons</h3>
          {[...lessons].sort((a, b) => a.order - b.order).map((l, i) => {
            const done = lessonsCompleted.includes(l.id);
            return (
              <button key={l.id} onClick={() => openLesson(l)} className="w-full flex items-center gap-3 bg-white rounded-lg border p-4 text-left hover:shadow-md transition" data-testid={`lesson-card-${i}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${done ? "bg-green-100" : "bg-gray-100"}`}>
                  {done ? <Check size={16} className="text-green-600" weight="bold" /> : <span className="text-xs font-bold text-[#6B7280]">{i + 1}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0F172A]">{l.title}</p>
                  <div className="flex gap-2 mt-0.5">
                    {l.video_url && <span className="text-[10px] text-blue-600 flex items-center gap-0.5"><Video size={10} /> Video</span>}
                    {(l.audio_url || l.voiceover_script) && <span className="text-[10px] text-purple-600 flex items-center gap-0.5"><SpeakerHigh size={10} /> Voiceover</span>}
                  </div>
                </div>
                <CaretRight size={16} className="text-[#C5A059]" />
              </button>
            );
          })}
        </div>

        {/* Assessment */}
        {questions.length > 0 && (
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[#0F172A]">Knowledge Check</h3>
                <p className="text-xs text-[#6B7280]">{questions.length} questions · Pass mark: {selectedModule.pass_mark || 80}%</p>
                {prog.assessment_score != null && (
                  <p className={`text-xs mt-1 font-medium ${prog.assessment_passed ? "text-green-600" : "text-red-500"}`}>
                    Score: {prog.assessment_score}% ({prog.assessment_passed ? "Passed" : "Failed"}) · Attempts: {prog.assessment_attempts}
                  </p>
                )}
              </div>
              <Button onClick={startAssessment} disabled={!allLessonsDone} className={allLessonsDone ? "bg-[#C5A059] hover:bg-[#B8933F]" : ""} data-testid="start-assessment-btn">
                {prog.assessment_passed ? "Retake" : allLessonsDone ? "Start Assessment" : "Complete all lessons first"}
              </Button>
            </div>
          </div>
        )}

        {/* Module Status */}
        {prog.status === "completed" && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trophy size={24} className="text-green-600" weight="duotone" />
              <div>
                <p className="text-sm font-semibold text-green-700">Module Completed</p>
                <p className="text-xs text-green-600">Completed on {prog.completed_at ? new Date(prog.completed_at).toLocaleDateString() : ""}</p>
              </div>
            </div>
            <Button onClick={goToNextModule} className="bg-[#C5A059] hover:bg-[#B8933F] gap-1" data-testid="module-next-module-btn">
              Next Module <ArrowRight size={14} />
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ============ DASHBOARD ============
  const pct = progress?.completion_percentage || 0;
  const nextMod = progress?.next_module;
  return (
    <div data-testid="academy-dashboard" className="space-y-6">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] rounded-xl p-6 md:p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#C5A059] opacity-5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap size={28} className="text-[#C5A059]" weight="duotone" />
            <span className="text-[10px] text-[#C5A059] font-semibold uppercase tracking-widest">Banks Morgan Academy</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold">Recruitment Academy</h1>
          <p className="text-sm text-[#94A3B8] mt-1 max-w-xl">Your training, development and pathway to becoming a successful Banks Morgan Recruitment Professional.</p>

          {/* Progress Bar */}
          <div className="mt-6 max-w-md">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-[#C5A059] uppercase tracking-wider">Training Progress</span>
              <span className="text-lg font-bold">{pct}%</span>
            </div>
            <div className="h-3 bg-[#1E293B] rounded-full overflow-hidden border border-[#334155]">
              <div className="h-full bg-gradient-to-r from-[#C5A059] to-[#D4B06A] rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-[#94A3B8] mt-1.5">{progress?.completed || 0} of {progress?.total_modules || 0} modules completed</p>
          </div>

          {nextMod && (
            <Button onClick={() => openModule(nextMod.id)} className="mt-5 bg-[#C5A059] hover:bg-[#B8933F] text-white gap-2" data-testid="continue-training-btn">
              <Play size={16} weight="bold" /> Continue Training
            </Button>
          )}
          {pct === 100 && (
            <Button onClick={goToRecruitmentHub} className="mt-5 bg-[#10B981] hover:bg-[#059669] text-white gap-2 ml-3" data-testid="go-to-hub-from-academy-btn">
              <Rocket size={16} weight="bold" /> Go to Recruitment Hub
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Completed", val: progress?.completed || 0, icon: Check, color: "#10B981" },
          { label: "In Progress", val: progress?.in_progress || 0, icon: BookOpen, color: "#3B82F6" },
          { label: "Remaining", val: progress?.not_started || 0, icon: Target, color: "#F59E0B" },
          { label: "Avg Score", val: `${progress?.average_score || 0}%`, icon: Star, color: "#C5A059" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg border p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + "14" }}>
              <s.icon size={20} style={{ color: s.color }} weight="duotone" />
            </div>
            <div>
              <p className="text-[10px] text-[#6B7280] uppercase font-medium">{s.label}</p>
              <p className="text-lg font-bold text-[#0F172A]">{s.val}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Status & Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-[10px] text-[#6B7280] uppercase font-medium">Status</p>
          <p className="text-sm font-bold text-[#0F172A] mt-1 capitalize">{(progress?.status || "not_started").replace("_", " ")}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-[10px] text-[#6B7280] uppercase font-medium">Est. Remaining</p>
          <p className="text-sm font-bold text-[#0F172A] mt-1">{progress?.remaining_minutes || 0} minutes</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-[10px] text-[#6B7280] uppercase font-medium">Certificates</p>
          <p className="text-sm font-bold text-[#0F172A] mt-1">{progress?.certificates_earned || 0}</p>
          {pct === 100 && (progress?.certificates_earned || 0) === 0 && (
            <Button size="sm" onClick={generateCertificate} className="mt-2 bg-[#C5A059] text-xs" data-testid="generate-cert-btn">Generate Certificate</Button>
          )}
          {(progress?.certificates_earned || 0) > 0 && (
            <Button size="sm" onClick={() => loadAndDownloadCert()} variant="outline" className="mt-2 text-xs gap-1" data-testid="download-cert-btn">Download Certificate</Button>
          )}
        </div>
      </div>

      {/* Module Grid */}
      <div>
        <h2 className="text-base font-bold text-[#0F172A] mb-3">Your Curriculum</h2>
        <div className="space-y-2">
          {loading ? [1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />) :
            modules.map((m, i) => {
              const p = m.progress;
              const status = p?.status || "not_started";
              const sc = STATUS_COLORS[status];
              const lessonsDone = p?.lessons_completed?.length || 0;
              const totalLessons = m.lessons?.length || 0;
              return (
                <button key={m.id} onClick={() => openModule(m.id)} className="w-full bg-white rounded-lg border p-4 text-left hover:shadow-md transition group" data-testid={`module-card-${i}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${status === "completed" ? "bg-green-100" : "bg-[#0F172A]"}`}>
                      {status === "completed" ? <Check size={20} className="text-green-600" weight="bold" /> : <span className="text-white font-bold text-sm">{m.order}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-[#0F172A] truncate group-hover:text-[#1E3A8A] transition">{m.title}</h3>
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${sc}`}>{status === "not_started" ? "Not Started" : status === "in_progress" ? "In Progress" : "Completed"}</span>
                      </div>
                      <p className="text-xs text-[#6B7280] mt-0.5 truncate">{m.description}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-[10px] text-[#6B7280]">{m.estimated_duration_minutes} min</span>
                        <span className="text-[10px] text-[#6B7280]">{totalLessons} lessons</span>
                        {p?.assessment_score != null && <span className={`text-[10px] font-medium ${p.assessment_passed ? "text-green-600" : "text-red-500"}`}>Score: {p.assessment_score}%</span>}
                      </div>
                      {status === "in_progress" && totalLessons > 0 && (
                        <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-xs">
                          <div className="h-full bg-[#C5A059] rounded-full" style={{ width: `${(lessonsDone / totalLessons) * 100}%` }} />
                        </div>
                      )}
                    </div>
                    <CaretRight size={18} className="text-[#C5A059] flex-shrink-0 group-hover:translate-x-1 transition" />
                  </div>
                </button>
              );
            })
          }
        </div>
      </div>
    </div>
  );
}
